import React from "react";
import { HeartHandshake, Users } from "lucide-react";
import useReveal from "@/hooks/useReveal";

/**
 * Seção "Sobre" reposicionada, cuidado de famílias e casais
 * através do Círculo Renascer. Mantém o ID "sobre" para compatibilidade
 * com a navegação. Layout 12-col com foto do casal à esquerda.
 *
 * Placeholder da foto de Carlos + Milena é temporário e pode ser
 * substituído trocando COUPLE_PHOTO para a URL definitiva.
 */
const COUPLE_PHOTO = "/couple-carlos-milena-cropped.jpg";

const BADGES = [
  { n: "4+ anos", t: "Dedicação ao Círculo Renascer" },
  { n: "Casais", t: "Aconselhados e fortalecidos" },
  { n: "Famílias", t: "Lares restaurados e reconciliados" },
  { n: "Testemunho", t: "Vidas transformadas por Deus" },
];

export default function About() {
  const r1 = useReveal();
  const r2 = useReveal();
  return (
    <section
      id="sobre"
      data-testid="about-section"
      className="pt-20 md:pt-24 lg:pt-28 pb-16 md:pb-20 bg-cream"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid md:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Foto side */}
          <div ref={r1} className="reveal md:col-span-5 relative">
            <div
              className="absolute -top-6 -left-6 w-40 h-40 bg-[#C5A059]/10 hidden md:block"
              aria-hidden
            />
            <div
              data-testid="about-couple-photo"
              className="relative aspect-[4/5] overflow-hidden bg-[#0B1E36]"
            >
              {COUPLE_PHOTO ? (
                <img
                  src={COUPLE_PHOTO}
                  alt="Carlos e Milena Medeiros"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <CouplePlaceholder />
              )}
            </div>
            <div
              className="absolute -bottom-6 -right-6 w-32 h-32 border border-[#0B1E36]/20 hidden md:block"
              aria-hidden
            />
          </div>

          {/* Texto side */}
          <div ref={r2} className="reveal md:col-span-7">
            <div className="flex items-center mb-6">
              <span className="accent-rule" />
              <span className="eyebrow">Círculo Renascer · Carlos e Milena</span>
            </div>

            <h2
              data-testid="about-headline"
              className="font-serif-display text-[#0B1E36] text-[2.5rem] sm:text-[2.9rem] md:text-5xl lg:text-[4.25rem] leading-[1.04] tracking-[-0.028em] font-medium"
            >
              Cuidando de famílias,{" "}
              <span className="italic text-[#C5A059]">restaurando casamentos</span>.
            </h2>

            <p
              data-testid="about-subtitle"
              className="mt-6 font-serif-display italic text-[#0B1E36]/75 text-[1.15rem] md:text-[1.35rem] leading-[1.5] tracking-[-0.008em] max-w-xl"
            >
              Uma missão que nasceu em Deus e tem alcançado vidas.
            </p>

            <div className="mt-10 space-y-6 text-[#2F374A] text-[1.05rem] md:text-[1.12rem] leading-[1.85] font-light">
              <p>
                Há mais de quatro anos, temos nos dedicado ao cuidado de casais
                através do Círculo Renascer.
              </p>
              <p>
                O que começou como um tempo de preparação se transformou em uma
                missão viva: acompanhar histórias reais, caminhar com pessoas
                em seus momentos mais difíceis e testemunhar a transformação
                que só Deus pode realizar.
              </p>
              <p>
                Ao longo dessa jornada, foram muitas vidas alcançadas, muitos
                lares restaurados e inúmeros testemunhos de reconstrução,
                perdão e libertação.
              </p>
              <p>
                Cada encontro, cada aconselhamento e cada oração fazem parte de
                um propósito maior que Deus tem confiado a nós:{" "}
                <span className="italic text-[#0B1E36]">
                  fortalecer famílias
                </span>{" "}
                e trazer esperança para relacionamentos que precisam recomeçar.
              </p>
              <p>
                Seguimos firmes nessa missão, com temor, responsabilidade e
                gratidão, sabendo que não se trata apenas de aconselhar, mas
                de servir como instrumento nas mãos de Deus para transformação
                de vidas.
              </p>
            </div>

            <div
              data-testid="about-badges"
              className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6"
            >
              {BADGES.map((b) => (
                <div
                  key={b.n}
                  className="border-l-2 border-[#C5A059] pl-4 py-1"
                >
                  <p className="font-serif-display text-[1.3rem] md:text-[1.5rem] text-[#0B1E36] leading-[1.15] tracking-[-0.01em]">
                    {b.n}
                  </p>
                  <p className="mt-1 text-[0.75rem] text-[#6B7280] leading-[1.5] font-light">
                    {b.t}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Placeholder elegante para a foto de Carlos + Milena enquanto a
 * imagem final não é fornecida. Tom institucional, dourado + serif.
 */
function CouplePlaceholder() {
  return (
    <div
      aria-label="Espaço reservado para a foto de Carlos e Milena Medeiros"
      className="relative w-full h-full"
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 30% 20%, rgba(197,160,89,0.24) 0%, transparent 55%), radial-gradient(ellipse at 80% 85%, rgba(197,160,89,0.18) 0%, transparent 60%), linear-gradient(155deg, #0B1E36 0%, #13294B 55%, #0B1E36 100%)",
        }}
        aria-hidden
      />
      <div className="absolute inset-5 border border-[#C5A059]/25" aria-hidden />

      <div className="relative h-full flex flex-col p-8 md:p-10 text-white">
        <div className="flex items-center gap-3">
          <span className="h-px w-8 bg-[#C5A059]" aria-hidden />
          <span className="text-[0.6rem] uppercase tracking-[0.32em] text-[#C5A059] font-medium">
            Círculo Renascer
          </span>
        </div>

        <div className="mt-auto">
          <div className="flex items-end gap-4 md:gap-5">
            <div className="relative">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#C5A059]/20 border border-[#C5A059]/40 flex items-center justify-center">
                <Users size={22} strokeWidth={1.3} className="text-[#C5A059]" />
              </div>
            </div>
            <HeartHandshake
              size={18}
              strokeWidth={1.4}
              className="text-[#C5A059] mb-2"
              aria-hidden
            />
            <div className="relative">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#C5A059]/20 border border-[#C5A059]/40 flex items-center justify-center">
                <Users size={22} strokeWidth={1.3} className="text-[#C5A059]" />
              </div>
            </div>
          </div>

          <p className="mt-8 font-serif-display italic text-white/95 text-[1.35rem] md:text-[1.55rem] leading-[1.3] tracking-[-0.01em]">
            Carlos <span className="text-[#C5A059]">&amp;</span> Milena
          </p>
          <p className="mt-2 text-[0.7rem] uppercase tracking-[0.24em] text-white/55 font-medium">
            Foto em breve
          </p>
        </div>
      </div>
    </div>
  );
}
